import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MessageInstance } from 'antd/es/message/interface';
import { getTheme } from 'helpers/getTheme';
import { IDataRoom } from 'interfaces';

export const defaultSidebarWidth = 320;
export const narrowSidebarWidth = 64;

interface DataState {
  selectedKeys: string[];
  docsSegment: string;
  messageApi: MessageInstance | null;
  isMoreInfoHide: boolean;

  isLoadingProcess: boolean;
  isDeletingDocuments: boolean;
  isUploadingDocuments: boolean;
  isUploadingSomeChat: boolean;
  isMovingDocument: boolean;
  displayedDragger: boolean;

  isOpenDeleteEntityModal: boolean;
  isOpenMoveEntityModal: boolean;
  isOpenInfoEntityModal: boolean;
  isOpenRenameEntityModal: boolean;
  isActiveContextMenu: boolean;
  isOpenShareEntityModal: boolean;
  isOpenNewVersionModal: boolean;
  isOpenSuccessfulPayment: boolean;
  isOpenFailedPayment: boolean;

  sideBarWidth: number;
  isResizing: boolean;
  isTourOpen: boolean;
  isDarkMode: boolean;
  logoutInProcess: boolean;

  isOpenConfidentConfirm: boolean
  isVisibleUploadModal: boolean;
  isFilesSearchingMode: boolean;
  isEditRoomModalOpen: boolean;
  isOpenPermissionsModal: boolean;
  isOpenChangePassword: boolean;
  isInviteUserOpen: boolean;
  isOpenPostfixModal: boolean;
  isProfileOpen: boolean;

  isRSidebarCollapsed: boolean;
  rSidebarEntity: string;
  rSidebarWidth: number;
  triggerNdaLeave: boolean;

  unlinkModalState: { open: boolean; service_name: string; in_progress: boolean };

  updateAfterDeleteTimer: NodeJS.Timeout | null;

  userMediaStream: MediaStream | null;

  isTariffInfoModalOpen: boolean;
  tariffInfoModalRoom: IDataRoom | null;

  soundNotificationState: { open: boolean; enabled: boolean; denied: boolean };
  playChatNotification: boolean;
  
  uploadDropdownOpen: boolean;
  addDropdownOpen: boolean;
}

const initialState: DataState = {
  selectedKeys: ['/documents'],
  docsSegment: 'view',
  isMoreInfoHide: true,

  messageApi: null,

  isLoadingProcess: false,
  isDeletingDocuments: false,
  isUploadingDocuments: false,
  isUploadingSomeChat: false,
  isMovingDocument: false,
  displayedDragger: false,

  isOpenDeleteEntityModal: false,
  isOpenMoveEntityModal: false,
  isOpenInfoEntityModal: false,
  isActiveContextMenu: false,
  isOpenRenameEntityModal: false,
  isOpenShareEntityModal: false,
  isOpenNewVersionModal: false,
  isOpenSuccessfulPayment: false,
  isOpenFailedPayment: false,
  isProfileOpen: false,

  sideBarWidth: localStorage.getItem('sideBarWidth') ? Number(localStorage.getItem('sideBarWidth')) : defaultSidebarWidth,
  isResizing: false,
  isTourOpen: false,
  isDarkMode: getTheme() === 'dark' ? true : false,
  logoutInProcess: false,

  isOpenConfidentConfirm: false,
  isVisibleUploadModal: false,
  isFilesSearchingMode: false,
  isEditRoomModalOpen: false,
  isOpenPermissionsModal: false,
  isOpenChangePassword: false,
  isInviteUserOpen:false,
  isOpenPostfixModal: false,

  isRSidebarCollapsed: true,
  rSidebarEntity: '',
  rSidebarWidth: 0,

  triggerNdaLeave: false,

  unlinkModalState: { open: false, service_name: '', in_progress: false },
  
  updateAfterDeleteTimer: null,

  userMediaStream: null,

  isTariffInfoModalOpen: false,
  tariffInfoModalRoom: null,

  soundNotificationState: { open: false, enabled: false, denied: localStorage.getItem('alreadyDeniedSound') === 'true' },
  playChatNotification: false,
  
  uploadDropdownOpen: false,
  addDropdownOpen: false,
};

const windowStateSlice = createSlice({
  name: 'windowState',
  initialState,
  reducers: {
    setSelectedKeys(state, action: PayloadAction<string[]>) {
      state.selectedKeys = action.payload;
    },
    setDocsSegment(state, action: PayloadAction<string>) {
      state.docsSegment = action.payload;
    },

    setMessageApi(state, action: PayloadAction<MessageInstance>) {
      state.messageApi = action.payload;
    },
    setMoreInfoHidden(state, action: PayloadAction<boolean>) {
      state.isMoreInfoHide = action.payload;
    },

    setIsLoadingProcess(state, action: PayloadAction<boolean>) {
      state.isLoadingProcess = action.payload;
    },
    setIsDeletingDocuments(state, action: PayloadAction<boolean>) {
      state.isDeletingDocuments = action.payload;
    },
    setIsUploadingDocuments(state, action: PayloadAction<boolean>) {
      state.isUploadingDocuments = action.payload;
    },

    setIsUploadingSomeChat(state, action: PayloadAction<boolean>) {
      state.isUploadingSomeChat = action.payload;
    },
    
    setDisplayedDragger(state, action: PayloadAction<boolean>) {
      state.displayedDragger = action.payload;
    },

    setMovingDocument(state, action: PayloadAction<boolean>) {
      state.isMovingDocument = action.payload;
    },
    setIsOpenDeleteEntityModal(state, action: PayloadAction<boolean>) {
      state.isOpenDeleteEntityModal = action.payload;
    },
    setIsOpenMoveEntityModal(state, action: PayloadAction<boolean>) {
      state.isOpenMoveEntityModal = action.payload;
    },
    setIsOpenInfoEntityModal(state, action: PayloadAction<boolean>) {
      state.isOpenInfoEntityModal = action.payload;
    },
    setIsActiveContextMenu(state, action: PayloadAction<boolean>) {
      state.isActiveContextMenu = action.payload;
    },
    setIsOpenRenameEntityModal(state, action: PayloadAction<boolean>) {
      state.isOpenRenameEntityModal = action.payload;
    },
    setIsOpenShareEntityModal(state, action: PayloadAction<boolean>) {
      state.isOpenShareEntityModal = action.payload;
    },
    setIsOpenNewVersionModal(state, action: PayloadAction<boolean>) {
      state.isOpenNewVersionModal = action.payload;
    },
    setIsOpenSuccessfulPayment(state, action: PayloadAction<boolean>) {
      state.isOpenSuccessfulPayment = action.payload;
    },
    setIsOpenFailedPayment(state, action: PayloadAction<boolean>) {
      state.isOpenFailedPayment = action.payload;
    },
    setIsProfileOpen(state, action: PayloadAction<boolean>) {
      state.isProfileOpen = action.payload;
    },
    setIsVisibleUploadModal(state, action: PayloadAction<boolean>) {
      state.isVisibleUploadModal = action.payload;
    },
    setIsFilesSearchingMode(state, action: PayloadAction<boolean>) {
      state.isFilesSearchingMode = action.payload;
    },

    setSideBarWidth(state, action: PayloadAction<number>) {
      state.sideBarWidth = action.payload;
    },
    setIsResizing(state, action: PayloadAction<boolean>) {
      state.isResizing = action.payload;
    },
    setIsTourOpen(state, action: PayloadAction<boolean>) {
      state.isTourOpen = action.payload;
    },
    setIsDarkMode(state, action: PayloadAction<boolean>) {
      state.isDarkMode = action.payload;
      if (action.payload) {
        localStorage.setItem('systemTheme', 'dark');
        // localStorage.setItem('darkTheme', 'true');
        localStorage.setItem('darkTheme', 'false');
      } else {
        localStorage.setItem('systemTheme', 'light');
        localStorage.setItem('darkTheme', 'false');
      }
    },

    setLogoutInProcess(state, action: PayloadAction<boolean>) {
      state.logoutInProcess = action.payload;
    },

    setIsOpenConfidentConfirm(state, action: PayloadAction<boolean>) {
      state.isOpenConfidentConfirm = action.payload;
    },
    setIsEditRoomModalOpen(state, action: PayloadAction<boolean>) {
      state.isEditRoomModalOpen = action.payload;
    },
    setIsOpenPermissionsModal(state, action: PayloadAction<boolean>) {
      state.isOpenPermissionsModal = action.payload;
    },
    setIsOpenChangePassword(state, action: PayloadAction<boolean>) {
      state.isOpenChangePassword = action.payload;
    },
    setIsInviteUserOpen(state, action: PayloadAction<boolean>) {
      state.isInviteUserOpen = action.payload;
    },
    setIsOpenPostfixModal(state, action: PayloadAction<boolean>) {
      state.isOpenPostfixModal = action.payload;
    },

    setTriggerNdaLeave(state, action: PayloadAction<boolean>) {
      state.triggerNdaLeave = action.payload;
    },

    setIsRSidebarCollapsed(
      state,
      action: PayloadAction<{
        collapsed: boolean;
        entity: string;
        width: number;
      }>
    ) {
      state.isRSidebarCollapsed = action.payload.collapsed;
      state.rSidebarEntity = action.payload.entity;
      state.rSidebarWidth = action.payload.width;
    },

    setUnlinkModalState(state, action: PayloadAction<{open: boolean; service_name: string; in_progress: boolean}>) {
      state.unlinkModalState = action.payload;
    },

    setUpdateAfterDeleteTimer(state, action: PayloadAction<NodeJS.Timeout | null>) {
      state.updateAfterDeleteTimer = action.payload;
    },

    setUserMediaStream(state, action: PayloadAction<MediaStream | null>) {
      state.userMediaStream = action.payload;
    },

    setSoundNotificationState(state, action: PayloadAction<{ open: boolean; enabled: boolean; denied: boolean }>) {
      state.soundNotificationState = action.payload;
    },
    needPlayChatNotification(state, action: PayloadAction<boolean>) {
      state.playChatNotification = action.payload;
    },

    setIsTariffInfoModalOpen(state, action: PayloadAction<boolean>) {
      state.isTariffInfoModalOpen = action.payload;
    },
    setTariffInfoModalRoom(state, action: PayloadAction<IDataRoom | null>) {
      state.tariffInfoModalRoom = action.payload;
    },
    setUploadDropdownOpen(state, action: PayloadAction<boolean>) {
      state.uploadDropdownOpen = action.payload;
    },
    setAddDropdownOpen(state, action: PayloadAction<boolean>) {
      state.addDropdownOpen = action.payload;
    },
  },
});

const windowStateReducer = windowStateSlice.reducer;

export const {
  setSelectedKeys,
  setDocsSegment,
  setMessageApi,
  setMoreInfoHidden,
  setIsLoadingProcess,
  setIsDeletingDocuments,
  setIsUploadingDocuments,
  setIsUploadingSomeChat,
  setDisplayedDragger,
  setMovingDocument,
  setIsOpenDeleteEntityModal,
  setIsOpenMoveEntityModal,
  setIsOpenInfoEntityModal,
  setIsActiveContextMenu,
  setIsOpenRenameEntityModal,
  setIsOpenShareEntityModal,
  setIsOpenNewVersionModal,
  setIsOpenSuccessfulPayment,
  setIsOpenChangePassword,
  setIsOpenFailedPayment,
  setIsProfileOpen,
  setIsVisibleUploadModal,
  setIsFilesSearchingMode,
  setIsOpenConfidentConfirm,
  setIsEditRoomModalOpen,
  setIsOpenPermissionsModal,
  setIsInviteUserOpen,
  setSideBarWidth,
  setIsResizing,
  setIsTourOpen,
  setIsDarkMode,
  setLogoutInProcess,
  setIsOpenPostfixModal,
  setTriggerNdaLeave,
  setIsRSidebarCollapsed,
  setUnlinkModalState,
  setUpdateAfterDeleteTimer,
  setUserMediaStream,
  setIsTariffInfoModalOpen,
  setTariffInfoModalRoom,
  setSoundNotificationState,
  needPlayChatNotification,
  setUploadDropdownOpen,
  setAddDropdownOpen,
} = windowStateSlice.actions;
export default windowStateReducer;
